// Macro-Forecaster API Wrapper — fetcht /trading/macro/*.

import { apiFetch, getApiBase } from './api';

export type Importance = 'low' | 'medium' | 'high';

export interface MacroRelease {
  indicator: string;
  country: string;
  release_time_et: string | null;
  previous: number | null;
  consensus: number | null;
  actual: number | null;
  unit: string | null;
  importance: Importance;
  source: string;
}

export type Dir = 'up' | 'down' | 'flat';

export const ASSET_KEYS = [
  'DXY',
  'EURUSD',
  'BTC',
  'NDX',
  'SPX',
  'DAX',
  'GOLD',
  'US10Y',
  'OIL',
] as const;
export type AssetKey = (typeof ASSET_KEYS)[number];

export interface AssetReaction {
  direction: Dir;
  /** Aktien/FX/Commodities in %, US10Y in bp. */
  magnitude_pct: number;
  confidence: number;
}

export interface PerEventScenario {
  label: 'beat' | 'inline' | 'miss';
  threshold: string;
  probability_pct: number;
  asset_reactions: Partial<Record<AssetKey, AssetReaction>>;
  one_liner: string;
}

export interface PerEventForecast {
  event_name: string;
  release_time_et: string;
  consensus: string;
  previous: string;
  priced_in_summary: string;
  scenarios: PerEventScenario[];
}

/** Legacy-Scenario (V1) — bleibt für Backward-Compat alter D1-Rows. */
export interface MacroScenario {
  label: 'beat' | 'inline' | 'miss';
  threshold: string;
  likely_reaction: string;
  spx_direction: Dir;
  ndx_direction: Dir;
  confidence: number;
}

export interface MacroForecast {
  id: string;
  generated_at: string;
  target_date: string;
  /** V2 (Doc-Profi-Format 2026-05-17). */
  pre_note?: string;
  events?: PerEventForecast[];
  /** V1 Fallback. */
  summary?: string;
  scenarios?: MacroScenario[];
  releases: MacroRelease[];
  overall_bias: 'bullish' | 'bearish' | 'neutral';
  rationale: string;
}

export interface TodayReleasesResponse {
  source: string;
  releases: MacroRelease[];
  error?: string;
}

export interface DatedMacroRelease extends MacroRelease {
  /** YYYY-MM-DD aus Finnhub `time`-Feld extrahiert. */
  date: string;
}

export interface UpcomingReleasesResponse {
  source: string;
  days: number;
  countries: string[];
  releases: DatedMacroRelease[];
  error?: string;
}

export type MacroCountry = 'US' | 'DE' | 'EU' | 'GB' | 'JP' | 'AU' | 'CA' | 'CH' | 'CN' | 'NZ';

export interface ForecastResponse {
  forecast: MacroForecast | null;
}

export async function fetchTodayReleases(accessToken: string): Promise<TodayReleasesResponse> {
  return apiFetch<TodayReleasesResponse>('/trading/macro/today', { accessToken });
}

export async function fetchUpcomingReleases(
  accessToken: string,
  days = 14,
  countries: ReadonlyArray<MacroCountry> = ['US', 'DE', 'EU'],
): Promise<UpcomingReleasesResponse> {
  const params = new URLSearchParams({
    days: String(days),
    countries: countries.join(','),
  });
  return apiFetch<UpcomingReleasesResponse>(`/trading/macro/upcoming?${params.toString()}`, {
    accessToken,
  });
}

export async function fetchLatestForecast(accessToken: string): Promise<ForecastResponse> {
  return apiFetch<ForecastResponse>('/trading/macro/forecast', { accessToken });
}

export async function triggerForecastNow(accessToken: string): Promise<{ saved: boolean; reason?: string }> {
  return apiFetch<{ saved: boolean; reason?: string }>('/trading/macro/run-now', {
    method: 'POST',
    accessToken,
  });
}

export type ImportanceFilter = 'all' | 'mid_high' | 'high';

/** Subscribe-URL für Outlook/Apple/Google Cal — Token im Query weil
 *  Cal-Clients keine Bearer-Header senden. Wird nur 1× konstruiert und
 *  vom User in seinen Kalender per „Per URL abonnieren" eingefügt.
 *  Default importance = mid_high (Doc 2026-05-17 — Low ist 90 % Lärm). */
export function buildCalendarSubscribeUrl(
  accessToken: string,
  options: {
    days?: number;
    countries?: ReadonlyArray<MacroCountry>;
    importance?: ImportanceFilter;
  } = {},
): string {
  const params = new URLSearchParams({
    token: accessToken,
    days: String(options.days ?? 30),
    countries: (options.countries ?? ['US', 'DE', 'EU']).join(','),
    importance: options.importance ?? 'mid_high',
  });
  return `${getApiBase()}/trading/macro/calendar.ics?${params.toString()}`;
}
